// Step 12 — Deal Situation panel.
//
// Renders the deterministic workspace-level Deal Situation aggregate:
// buyer portfolio, cross-buyer patterns, silence signals, preparedness
// observations, and suggested actions (always tagged as suggestions).
//
// The component is a read-only surface. Nothing here mutates state
// except the "Spara ögonblicksbild" button, which POSTs to the
// append-only snapshot log — no email is ever sent from this UI.

import { useCallback, useEffect, useState } from "react";

import {
  captureDealSituationSnapshot,
  createActionFromSuggestion,
  getDealSituationLive,
} from "./api";

const KIND_LABELS = {
  fact: "FAKT",
  inference: "INF",
};

export default function DealSituationPanel({ workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDealSituationLive(workspaceId);
      setData(res);
    } catch (e) {
      setError(e?.body?.detail || e.message || "Kunde inte ladda affärsläget");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const [creatingKind, setCreatingKind] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const handleCreateActionsFromSuggestion = useCallback(
    async (suggestion) => {
      if (!workspaceId || !suggestion?.kind) return;
      setCreatingKind(suggestion.kind);
      setCreateMessage(null);
      try {
        // Suggestions live inside the persisted snapshot payload, so
        // we first ensure there IS a current snapshot. The capture
        // endpoint is idempotent — it either creates a new row or
        // returns the latest matching one.
        const snap = await captureDealSituationSnapshot(workspaceId);
        const res = await createActionFromSuggestion(workspaceId, {
          snapshot_id: snap.id,
          suggestion_kind: suggestion.kind,
        });
        const n = res?.created_count ?? 0;
        setCreateMessage(
          n > 0
            ? `${n} åtgärd${n === 1 ? "" : "er"} skapade från förslaget.`
            : "Inga nya åtgärder skapade (redan öppna motsvarigheter finns).",
        );
      } catch (e) {
        setCreateMessage(
          e?.body?.detail || e.message || "Kunde inte skapa åtgärd.",
        );
      } finally {
        setCreatingKind(null);
      }
    },
    [workspaceId],
  );

  const handleCapture = useCallback(async () => {
    if (!workspaceId || saving) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await captureDealSituationSnapshot(workspaceId);
      if (res?.created) {
        setSaveMessage(`Ögonblicksbild v${res.version} sparad.`);
      } else {
        setSaveMessage(
          `Ingen förändring sedan senaste ögonblicksbild (v${res.version}).`,
        );
      }
    } catch (e) {
      setSaveMessage(
        e?.body?.detail || e.message || "Kunde inte spara ögonblicksbild.",
      );
    } finally {
      setSaving(false);
    }
  }, [workspaceId, saving]);

  if (!workspaceId) return null;
  if (loading && !data) {
    return (
      <div className="qai-ds-panel">
        <div className="qai-ds-header">Affärsläge</div>
        <div className="qai-ds-loading">Laddar…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="qai-ds-panel">
        <div className="qai-ds-header">Affärsläge</div>
        <div className="qai-ds-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const {
    buyer_portfolio: portfolio = [],
    cross_buyer_patterns: patterns = {},
    preparedness_observations: preparedness = [],
    communication_summary: comm = {},
    suggested_actions: suggestions = [],
    assistive_only: disclaimer,
  } = data;

  return (
    <div className="qai-ds-panel">
      <div className="qai-ds-header">
        <span>Affärsläge</span>
        <button
          type="button"
          className="qai-ds-capture"
          onClick={handleCapture}
          disabled={saving}
          title="Sparar en ögonblicksbild av affärsläget. Skickar inte något."
        >
          {saving ? "Sparar…" : "Spara ögonblicksbild"}
        </button>
      </div>

      {saveMessage && <div className="qai-ds-save-msg">{saveMessage}</div>}

      <div className="qai-ds-summary">
        <div className="qai-ds-summary-row">
          <span className="qai-ds-summary-label">Köpare i portföljen</span>
          <span className="qai-ds-summary-value">{comm.buyer_count ?? 0}</span>
        </div>
        <div className="qai-ds-summary-row">
          <span className="qai-ds-summary-label">Utskickade</span>
          <span className="qai-ds-summary-value">
            {comm.outbound_sent_total ?? 0} / {comm.outbound_total ?? 0}
          </span>
        </div>
        <div className="qai-ds-summary-row">
          <span className="qai-ds-summary-label">Inkommande</span>
          <span className="qai-ds-summary-value">{comm.inbound_total ?? 0}</span>
        </div>
        {comm.bounce_total > 0 && (
          <div className="qai-ds-summary-row">
            <span className="qai-ds-summary-label">Leveransfel</span>
            <span className="qai-ds-summary-value">{comm.bounce_total}</span>
          </div>
        )}
      </div>

      {portfolio.length > 0 && (
        <section className="qai-ds-section">
          <h4>Köpare</h4>
          <ul className="qai-ds-buyer-list">
            {portfolio.map((row) => (
              <li key={row.acquirer.id} className="qai-ds-buyer-row">
                <div className="qai-ds-buyer-head">
                  <span className="qai-ds-buyer-name">
                    {row.acquirer.name}
                  </span>
                  {row.interest_signal && (
                    <span className="qai-ds-buyer-signal">
                      <span className="qai-ds-tag qai-ds-tag-inf">
                        {KIND_LABELS[row.interest_signal.fact_or_inference] ||
                          row.interest_signal.fact_or_inference}
                      </span>
                      {row.interest_signal.value}
                    </span>
                  )}
                  {row.silence?.is_silent && (
                    <span className="qai-ds-silence" title="Ingen respons efter senaste utskick">
                      Tyst i {row.silence.days_since_last_outbound} dagar
                    </span>
                  )}
                </div>
                <div className="qai-ds-buyer-comm">
                  {row.communication.outbound_sent_count} skickade ·{" "}
                  {row.communication.inbound_count} svar ·{" "}
                  {row.open_items_count} öppna poster
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(patterns.information_requested?.length > 0 ||
        patterns.objection?.length > 0 ||
        patterns.requirement?.length > 0 ||
        patterns.question?.length > 0) && (
        <section className="qai-ds-section">
          <h4>Återkommande mönster</h4>
          <PatternGroup
            title="Informationsförfrågningar"
            items={patterns.information_requested}
          />
          <PatternGroup title="Invändningar" items={patterns.objection} />
          <PatternGroup title="Krav" items={patterns.requirement} />
          <PatternGroup title="Frågor" items={patterns.question} />
        </section>
      )}

      {preparedness.length > 0 && (
        <section className="qai-ds-section">
          <h4>Observerade gap</h4>
          <ul className="qai-ds-gap-list">
            {preparedness.map((obs, i) => (
              <li key={i} className="qai-ds-gap-row">
                <span className="qai-ds-tag qai-ds-tag-inf">INF</span>
                {obs.label}
              </li>
            ))}
          </ul>
        </section>
      )}

      {suggestions.length > 0 && (
        <section className="qai-ds-section">
          <h4>Förslag</h4>
          <div className="qai-ds-suggest-note">
            Endast förslag — inget utförs automatiskt.
          </div>
          {createMessage && (
            <div className="qai-ds-save-msg">{createMessage}</div>
          )}
          <ul className="qai-ds-suggest-list">
            {suggestions.map((s, i) => (
              <li key={i} className="qai-ds-suggest-row">
                <div className="qai-ds-suggest-label">{s.label}</div>
                {s.detail && (
                  <div className="qai-ds-suggest-detail">{s.detail}</div>
                )}
                <button
                  type="button"
                  className="qai-ds-capture qai-ds-suggest-btn"
                  onClick={() => handleCreateActionsFromSuggestion(s)}
                  disabled={creatingKind === s.kind}
                  title={
                    "Skapar en (eller flera) DealAction-poster i status "
                    + "'proposed'. Ingen kommunikation utförs."
                  }
                >
                  {creatingKind === s.kind
                    ? "Skapar…"
                    : "Skapa åtgärd"}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {disclaimer && (
        <div className="qai-ds-disclaimer">{disclaimer}</div>
      )}
    </div>
  );
}

function PatternGroup({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="qai-ds-pattern-group">
      <div className="qai-ds-pattern-title">{title}</div>
      <ul className="qai-ds-pattern-list">
        {items.map((p, i) => (
          <li key={i} className="qai-ds-pattern-row">
            <span className="qai-ds-tag qai-ds-tag-inf">INF</span>
            <span className="qai-ds-pattern-text">"{p.normalized_text}"</span>
            <span className="qai-ds-pattern-count">
              {p.acquirer_count} köpare
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
